import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'

import { useUsers } from 'api/aws/useUsers'
import {
  RecipientList,
  useNotificationRecipients,
  useSaveNotificationRecipients,
} from 'api/aws/notifications/useNotificationRecipients'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { User } from 'types'

interface NotificationRecipientsModalProps {
  open: boolean
  onClose: () => void
}

/** Slack user id to a display name, falling back to the raw id so a person who
 *  has left the workspace still shows as something a human can act on. */
const useUserLookup = (users: User[]): ((id: string) => User | undefined) => {
  return useMemo(() => {
    const byId = new Map<string, User>()
    for (const u of users) {
      if (u?.user_uuid) byId.set(u.user_uuid, u)
    }
    return (id: string): User | undefined => {
      return byId.get(id)
    }
  }, [users])
}

const RecipientRow: React.FC<{
  list: RecipientList
  users: User[]
  selectedIds: string[]
  onChange: (key: string, userIds: string[]) => void
  lookup: (id: string) => User | undefined
}> = ({ list, users, selectedIds, onChange, lookup }) => {
  const theme = useTheme()
  const selectedUsers = selectedIds.map(lookup).filter((u): u is User => Boolean(u))
  // Ids with no matching user record: someone who left, or a stale entry.
  const unknownIds = selectedIds.filter(id => !lookup(id))

  return (
    <Box sx={{ py: 2 }}>
      <Stack direction='row' alignItems='baseline' spacing={1} sx={{ mb: 0.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{list.label}</Typography>
        {list.usingDefaults && (
          <Tooltip title='Nothing has been saved for this list yet, so it is using the value from the backend config.'>
            <Chip label='default' size='small' sx={{ height: 20, fontSize: 11 }} />
          </Tooltip>
        )}
      </Stack>

      <Typography sx={{ fontSize: 13, color: theme.palette.text.secondary, mb: 1.5 }}>
        {list.description}
      </Typography>

      <Autocomplete
        multiple
        options={users}
        value={selectedUsers}
        getOptionLabel={option => option?.name || ''}
        isOptionEqualToValue={(option, value) => option?.user_uuid === value?.user_uuid}
        onChange={(_, newValue) => {
          onChange(
            list.key,
            newValue.map(u => u.user_uuid)
          )
        }}
        renderOption={(props, option) => (
          <Box component='li' {...props} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <Avatar src={option.image_512} alt={option.name} sx={{ height: 28, width: 28 }} />
            <Box>
              <Typography sx={{ fontSize: 14 }}>{option.name}</Typography>
              {option.title && (
                <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary }}>{option.title}</Typography>
              )}
            </Box>
          </Box>
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const { key, ...rest } = getTagProps({ index })
            return (
              <Chip
                {...rest}
                key={key}
                avatar={<Avatar src={option.image_512} alt={option.name} />}
                label={option.name}
                size='small'
              />
            )
          })
        }
        renderInput={params => (
          <TextField {...params} label={`Add to ${list.label.toLowerCase()}`} variant='outlined' size='small' />
        )}
      />

      {unknownIds.length > 0 && (
        <Alert severity='warning' sx={{ mt: 1, fontSize: 12, py: 0 }}>
          {unknownIds.length} id{unknownIds.length === 1 ? '' : 's'} in this list match no current user:{' '}
          {unknownIds.join(', ')}. They will keep receiving nothing until removed.
        </Alert>
      )}

      <Stack direction='row' spacing={2} sx={{ mt: 1 }}>
        <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary }}>
          {list.dealTypes.length === 0 ? 'All deal types' : list.dealTypes.join(', ')}
        </Typography>
        {list.updatedAt && (
          <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary }}>
            Last changed {new Date(list.updatedAt).toLocaleDateString()}
            {list.updatedBy ? ` by ${lookup(list.updatedBy)?.name ?? list.updatedBy}` : ''}
          </Typography>
        )}
      </Stack>
    </Box>
  )
}

const NotificationRecipientsModal: React.FC<NotificationRecipientsModalProps> = ({ open, onClose }) => {
  const theme = useTheme()
  const { showSnackbar } = useSnackbarContext()
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('currentUser') ?? 'null')
    } catch {
      return null
    }
  }, [])

  const { users = [] } = useUsers(open, ['notificationRecipients', 'users']) as { users: User[] }
  const lookup = useUserLookup(users)
  const {
    data: lists,
    isLoading,
    isError,
    error,
  } = useNotificationRecipients(currentUser?.user_uuid, open)
  const { mutate: save, isPending } = useSaveNotificationRecipients()

  // Local edit buffer, seeded from the server each time the modal opens.
  const [draft, setDraft] = useState<Record<string, string[]>>({})

  useEffect(() => {
    if (!lists) return
    const next: Record<string, string[]> = {}
    for (const list of lists) {
      next[list.key] = [...list.userIds]
    }
    setDraft(next)
  }, [lists])

  const changedKeys = useMemo(() => {
    if (!lists) return []
    return lists
      .filter(list => {
        const before = [...list.userIds].sort().join(',')
        const after = [...(draft[list.key] ?? [])].sort().join(',')
        return before !== after
      })
      .map(list => list.key)
  }, [lists, draft])

  const handleChange = (key: string, userIds: string[]): void => {
    setDraft(prev => ({ ...prev, [key]: userIds }))
  }

  const handleSave = (): void => {
    const payload = changedKeys.map(key => ({ key, userIds: draft[key] ?? [] }))
    if (payload.length === 0) {
      onClose()
      return
    }
    save(
      { lists: payload, currentUser: { user_uuid: currentUser?.user_uuid } },
      {
        onSuccess: () => {
          showSnackbar(
            `Notification recipients updated (${payload.length} list${payload.length === 1 ? '' : 's'})`,
            'success'
          )
          onClose()
        },
        onError: err => {
          showSnackbar(`Could not save notification recipients: ${(err as Error).message}`, 'error')
        },
      }
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>Deal Notification Recipients</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: theme.palette.text.secondary, mb: 1 }}>
          Everyone here gets a Slack DM when a matching deal is won. Admins are always notified and are not listed,
          so removing yourself here will not stop your own notifications.
        </Typography>

        {isLoading && (
          <Stack alignItems='center' sx={{ py: 6 }}>
            <CircularProgress size={28} />
          </Stack>
        )}

        {isError && (
          <Alert severity='error' sx={{ my: 2 }}>
            {(error as { response?: { status?: number } })?.response?.status === 403
              ? 'Admin access is required to view or change notification recipients.'
              : 'Could not load notification recipients.'}
          </Alert>
        )}

        {lists?.map((list, index) => (
          <React.Fragment key={list.key}>
            {index > 0 && <Divider />}
            <RecipientRow
              list={list}
              users={users}
              selectedIds={draft[list.key] ?? []}
              onChange={handleChange}
              lookup={lookup}
            />
          </React.Fragment>
        ))}
      </DialogContent>
      <DialogActions sx={{ pr: 3, pb: 2 }}>
        <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary, mr: 'auto', pl: 2 }}>
          {changedKeys.length === 0 ? 'No changes' : `${changedKeys.length} list(s) changed`}
        </Typography>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant='contained' color='primary' onClick={handleSave} disabled={isPending || changedKeys.length === 0}>
          {isPending ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default NotificationRecipientsModal
